import Phaser from 'phaser';
import { ASSET_KEYS, COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import {
  finnEtasje,
  type EtasjeDef,
  type GangsoneDef,
  type SoneRel,
} from '@/data/building';
import { effektivStatus, findFloor, type FloorId } from '@/data/floors';
import { TIDSKOSTNADER } from '@/data/timeCosts';
import { npcIRom } from '@/data/schedules';
import { initialerAv } from '@/data/portretter';
import { gameState } from '@/state/store';
import { gameClock } from '@/systems/gameClock';
import { sfx } from '@/systems/sfx';
import { haptisk } from '@/systems/haptisk';
import { foretrekkerRedusertBevegelse } from '@/systems/motionPreferanser';
import { SKRITT_PER_SEK, gangePose, skrittIndeks } from '@/systems/gange';
import { ToastManager } from '@/ui/Toast';

const HUD_HOYDE = 100;
const BANNER_HOYDE = 92;
const AVATAR_HOYDE = 280;
const GANGFART_PX_PER_MS = 0.55;
const WALK_ANIM = 'player-walk-anim';

export interface KorridorSceneData {
  etasjeNr: number;
  ankomstViaHeis?: boolean;
}

interface SkjermRekt {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface DorDef {
  rekt: SkjermRekt;
  type: 'heis' | 'rom' | 'laast';
  rom?: FloorId;
  label: string;
  hint?: string;
}

/**
 * Korridoren (fase 2, Mad TV-mekanikk) — én per etasje, generert fra
 * data/building.ts. Bakgrunnskunsten ER dørene: interaktive soner
 * (hitboxer i bakgrunnsandeler fra building.ts) ligger over de malte
 * dørene, med skilt og en diskret kontur som affordance — aldri fylte
 * rektangler. Avataren går i gulvets gangbane med fot-anker, skygge
 * og perspektivskala.
 */
export class KorridorScene extends Phaser.Scene {
  private etasje?: EtasjeDef;
  private ankomstViaHeis = false;
  private avatar?: Phaser.GameObjects.Container;
  private avatarFigur?: Phaser.GameObjects.Container;
  private avatarSkygge?: Phaser.GameObjects.Ellipse;
  private avatarWalk?: Phaser.GameObjects.Sprite;
  private avatarTween?: Phaser.Tweens.Tween;
  private avatarRetning = 1;
  private sisteSkritt = 0;
  private dorer: DorDef[] = [];
  private toasts?: ToastManager;
  private opptatt = false;
  /** Bakgrunnens faktiske renderte plassering — all sone-mapping går via denne. */
  private bgRekt: SkjermRekt = { x: 0, y: 0, w: GAME_WIDTH, h: GAME_HEIGHT };

  constructor() {
    super('Korridor');
  }

  init(data: KorridorSceneData): void {
    this.etasje = finnEtasje(data?.etasjeNr ?? 5);
    this.ankomstViaHeis = !!data?.ankomstViaHeis;
    this.opptatt = false;
    this.dorer = [];
  }

  create(): void {
    const etasje = this.etasje ?? finnEtasje(5)!;
    const erKapelleDamm = etasje.nr === 7;

    this.tegnBakgrunn(erKapelleDamm);
    this.toasts = new ToastManager(this);
    this.tegnBanner(etasje, erKapelleDamm);
    this.byggDorer(etasje, erKapelleDamm);
    this.byggNpcVedDorene(etasje);
    this.byggAvatar(etasje.korridor.gangsone);
    this.byggTapToMove(etasje.korridor.gangsone);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.avatarTween?.stop();
      this.avatarTween = undefined;
      this.toasts?.destroy();
      this.toasts = undefined;
    });
  }

  // ---------- Bakgrunn + koordinat-hjelpere ----------

  /**
   * Cover-by-width: bakgrunnen fyller alltid viewport-bredden; topp/bunn
   * beskjæres heller enn å vise striper. bgRekt holder faktisk rendret
   * posisjon/størrelse — bgX/bgY/bgB/bgH er ENESTE vei fra
   * bakgrunnsandeler til skjermkoordinater.
   */
  private tegnBakgrunn(erKapelleDamm: boolean): void {
    const tekstur = erKapelleDamm ? ASSET_KEYS.envKorridorKapelleDamm : ASSET_KEYS.envKorridor;
    if (this.textures.exists(tekstur)) {
      const tex = this.textures.get(tekstur).getSourceImage();
      const w = (tex as HTMLImageElement).width || 1;
      const h = (tex as HTMLImageElement).height || 1;
      const skala = GAME_WIDTH / w;
      const visH = h * skala;
      const offY = (GAME_HEIGHT - visH) / 2;
      this.add
        .image(0, offY, tekstur)
        .setOrigin(0, 0)
        .setScale(skala)
        .setDepth(0);
      this.bgRekt = { x: 0, y: offY, w: GAME_WIDTH, h: visH };
      return;
    }
    // Fallback uten tekstur: flat vegg + gulv i palettfarger. Ingen
    // dør-sprites — sonene (skilt + kontur) gjør dørene synlige.
    this.bgRekt = { x: 0, y: 0, w: GAME_WIDTH, h: GAME_HEIGHT };
    const vegg = erKapelleDamm ? 0xdfe3e8 : 0xfdfaf3;
    const gulv = erKapelleDamm ? 0x8a939e : 0xd9b382;
    const gulvY = this.bgY(0.78);
    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, vegg, 1).setOrigin(0, 0).setDepth(0);
    this.add
      .rectangle(0, gulvY, GAME_WIDTH, GAME_HEIGHT - gulvY, gulv, 1)
      .setOrigin(0, 0)
      .setDepth(0);
  }

  private bgX(rel: number): number {
    return this.bgRekt.x + rel * this.bgRekt.w;
  }

  private bgY(rel: number): number {
    return this.bgRekt.y + rel * this.bgRekt.h;
  }

  private soneTilSkjerm(sone: SoneRel): SkjermRekt {
    return {
      x: this.bgX(sone.x),
      y: this.bgY(sone.y),
      w: sone.bredde * this.bgRekt.w,
      h: sone.hoyde * this.bgRekt.h,
    };
  }

  // ---------- Banner ----------

  /** Smalt banner under HUD — tittelen skal ikke ligge midt i kunsten. */
  private tegnBanner(etasje: EtasjeDef, erKapelleDamm: boolean): void {
    this.add
      .rectangle(0, HUD_HOYDE, GAME_WIDTH, BANNER_HOYDE, erKapelleDamm ? 0x1f2d44 : 0xfdfaf3, 0.92)
      .setOrigin(0, 0)
      .setDepth(20);
    this.add
      .rectangle(0, HUD_HOYDE + BANNER_HOYDE - 3, GAME_WIDTH, 3, erKapelleDamm ? 0x9aa3ad : 0x862736, 0.8)
      .setOrigin(0, 0)
      .setDepth(20);
    this.add
      .text(24, HUD_HOYDE + 14, etasje.navn, {
        fontFamily: FONTS.display,
        fontSize: fontStr(28),
        color: erKapelleDamm ? '#f4ead5' : '#862736',
      })
      .setOrigin(0, 0)
      .setDepth(21);
    this.add
      .text(24, HUD_HOYDE + 58, etasje.beskrivelse, {
        fontFamily: FONTS.body,
        fontSize: fontStr(15),
        color: erKapelleDamm ? '#dfe3e8' : '#3a2e22',
      })
      .setOrigin(0, 0)
      .setDepth(21);
  }

  // ---------- Dører (soner over malt kunst) ----------

  private byggDorer(etasje: EtasjeDef, erKapelleDamm: boolean): void {
    const k = etasje.korridor;
    this.dorer.push({
      rekt: this.soneTilSkjerm(k.heis),
      type: 'heis',
      label: 'Heis',
    });

    type DorKilde = { rom?: FloorId; label: string; hint?: string; laast: boolean };
    const kilder: DorKilde[] = [];
    for (const romId of etasje.rom) {
      const floor = findFloor(romId);
      const status = floor
        ? effektivStatus(floor, {
            antallUtgivelser: gameState.antallUtgivelser(),
            spillerTier: gameState.tier,
          })
        : 'laast';
      kilder.push({
        rom: romId,
        label: floor?.label ?? romId,
        laast: status !== 'aapen',
        hint:
          status === 'aapen'
            ? undefined
            : floor?.unlockHint
              ? `Låst (${floor.unlockHint})`
              : 'Låst',
      });
    }
    for (const d of etasje.laasteDorer ?? []) {
      kilder.push({ label: d.label, hint: d.hint, laast: true });
    }

    kilder.forEach((kilde, i) => {
      const sone = k.dorSoner[i];
      if (!sone) {
        console.warn(`[korridor] mangler dørsone for ${kilde.label} på etasje ${etasje.nr}`);
        return;
      }
      this.dorer.push({
        rekt: this.soneTilSkjerm(sone),
        type: kilde.laast ? 'laast' : 'rom',
        rom: kilde.rom,
        label: kilde.label,
        hint: kilde.hint,
      });
    });

    for (const dor of this.dorer) {
      this.tegnDor(dor, erKapelleDamm);
    }
  }

  /** Affordance: skilt over døra + diskret lys kontur ved hover/tap. */
  private tegnDor(dor: DorDef, erKapelleDamm: boolean): void {
    const { x, y, w, h } = dor.rekt;
    const laast = dor.type === 'laast';

    // Konturen — aldri fylt; tynn lys ramme som lyser ved hover/tap.
    const kontur = this.add
      .rectangle(x - 4, y - 4, w + 8, h + 8, 0xffffff, 0)
      .setOrigin(0, 0)
      .setStrokeStyle(4, laast ? 0x9aa3ad : 0xf4d58d, 0.95)
      .setDepth(4)
      .setAlpha(0);

    const hitbox = this.add
      .rectangle(x, y, w, h, 0xffffff, 0.001)
      .setOrigin(0, 0)
      .setDepth(5);
    hitbox.setInteractive({ useHandCursor: !laast });
    hitbox.on('pointerover', () => kontur.setAlpha(1));
    hitbox.on('pointerout', () => kontur.setAlpha(0));
    hitbox.on('pointerdown', () => kontur.setAlpha(1));
    hitbox.on('pointerup', () => {
      this.time.delayedCall(220, () => kontur.setAlpha(0));
      this.gaaTilDor(dor);
    });

    // Skilt rett over hitboxen — klemmes innenfor skjermen.
    const skiltX = Phaser.Math.Clamp(x + w / 2, 150, GAME_WIDTH - 150);
    this.add
      .text(skiltX, y - 14, dor.label, {
        fontFamily: FONTS.display,
        fontSize: fontStr(22),
        color: laast ? '#6b6b6b' : erKapelleDamm ? '#f4ead5' : '#1a1a1a',
        align: 'center',
        wordWrap: { width: w + 80 },
        backgroundColor: erKapelleDamm ? 'rgba(31,45,68,0.88)' : 'rgba(253,250,243,0.88)',
        padding: { left: 10, right: 10, top: 4, bottom: 4 },
      })
      .setOrigin(0.5, 1)
      .setDepth(6);

    if (dor.hint) {
      this.add
        .text(skiltX, y + h + 12, dor.hint, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(14),
          color: erKapelleDamm ? '#dfe3e8' : '#6b6b6b',
          align: 'center',
          wordWrap: { width: w + 90 },
          backgroundColor: erKapelleDamm ? 'rgba(31,45,68,0.7)' : 'rgba(253,250,243,0.7)',
          padding: { left: 8, right: 8, top: 3, bottom: 3 },
        })
        .setOrigin(0.5, 0)
        .setDepth(6);
    }
  }

  /** NPC-er synlige fra korridoren — på gulvet ved døra de står bak. */
  private byggNpcVedDorene(etasje: EtasjeDef): void {
    const gulvY = this.bgY(etasje.korridor.gangsone.minY) + 18;
    for (const dor of this.dorer) {
      if (dor.type !== 'rom' || !dor.rom) continue;
      const tilstede = npcIRom(dor.rom, gameClock.naa, gameState.dag).filter(
        (n) => !gameState.harFlag(`stall_signert_${n.id}`),
      );
      tilstede.slice(0, 2).forEach((npc, i) => {
        const x = Phaser.Math.Clamp(
          dor.rekt.x + dor.rekt.w + 30 + i * 78,
          80,
          GAME_WIDTH - 80,
        );
        const pKey = npc.portrettKey ?? '';
        if (pKey && this.textures.exists(pKey)) {
          const img = this.add.image(x, gulvY, pKey).setOrigin(0.5, 1).setDepth(gulvY);
          const tex = this.textures.get(pKey).getSourceImage();
          const h = (tex as HTMLImageElement).height || 1;
          img.setScale(120 / h);
        } else {
          const ring = this.add.circle(x, gulvY - 60, 34, COLORS.beige, 1).setDepth(gulvY);
          ring.setStrokeStyle(3, COLORS.wine, 0.9);
          this.add
            .text(x, gulvY - 60, initialerAv(npc.navn), {
              fontFamily: FONTS.display,
              fontSize: fontStr(22),
              color: '#862736',
            })
            .setOrigin(0.5)
            .setDepth(gulvY);
        }
        this.add
          .text(x, gulvY + 6, npc.navn, {
            fontFamily: FONTS.hud,
            fontSize: fontStr(13),
            color: '#3a2e22',
            backgroundColor: 'rgba(253,250,243,0.85)',
            padding: { left: 6, right: 6, top: 2, bottom: 2 },
          })
          .setOrigin(0.5, 0)
          .setDepth(gulvY);
      });
    }
  }

  // ---------- Avatar (fot-anker, skygge, perspektiv, depth) ----------

  private byggAvatar(gangsone: GangsoneDef): void {
    const startX = this.ankomstViaHeis ? this.bgX(0.14) : GAME_WIDTH / 2;
    const startY = this.bgY((gangsone.minY + gangsone.maxY) / 2);
    // Container-origo ER fotpunktet: alt innhold tegnes med føttene i (0,0).
    this.avatar = this.add.container(startX, startY);

    // Skygge-ellipsen selger bakkekontakten — alltid synlig under føttene.
    this.avatarSkygge = this.add.ellipse(0, 4, 130, 34, 0x000000, 0.15);
    this.avatar.add(this.avatarSkygge);

    // Figuren ligger i sin egen container så gange-posen (bob/vugg) kan
    // legges på den uten å flytte fotpunktet eller skyggen.
    this.avatarFigur = this.add.container(0, 0);
    this.avatarWalk = undefined;
    if (this.textures.exists(ASSET_KEYS.playerWalk)) {
      // Ekte walk-cycle (AutoSprite): frame 0 i ro, animasjon under gange.
      if (!this.anims.exists(WALK_ANIM)) {
        this.anims.create({
          key: WALK_ANIM,
          frames: this.anims.generateFrameNumbers(ASSET_KEYS.playerWalk, {
            start: 0,
            end: 7,
          }),
          // 8 frames = 2 skritt per syklus → 4 frames per skritt.
          frameRate: 4 * SKRITT_PER_SEK,
          repeat: -1,
        });
      }
      this.avatarWalk = this.add
        .sprite(0, 0, ASSET_KEYS.playerWalk, 0)
        .setOrigin(0.5, 1)
        .setScale(AVATAR_HOYDE / 512);
      this.avatarFigur.add(this.avatarWalk);
    } else if (this.textures.exists(ASSET_KEYS.playerFigur)) {
      const img = this.add.image(0, 0, ASSET_KEYS.playerFigur).setOrigin(0.5, 1);
      const tex = this.textures.get(ASSET_KEYS.playerFigur).getSourceImage();
      const h = (tex as HTMLImageElement).height || 1;
      img.setScale(AVATAR_HOYDE / h);
      this.avatarFigur.add(img);
    } else {
      const hode = this.add.circle(0, -AVATAR_HOYDE + 36, 34, 0xe8c4a0, 1);
      const kropp = this.add
        .rectangle(0, -AVATAR_HOYDE + 76, 76, 110, COLORS.wine, 1)
        .setOrigin(0.5, 0);
      const bein = this.add
        .rectangle(0, -AVATAR_HOYDE + 186, 56, AVATAR_HOYDE - 186, 0x2e2a26, 1)
        .setOrigin(0.5, 0);
      this.avatarFigur.add([bein, kropp, hode]);
    }
    this.avatar.add(this.avatarFigur);

    this.oppdaterAvatarPerspektiv(gangsone);
  }

  /** Legg gange-posen på figuren — fotpunkt og skygge står igjen på gulvet. */
  private oppdaterGange(elapsedMs: number): void {
    if (!this.avatarFigur || !this.avatarSkygge) return;
    const pose = gangePose(elapsedMs);
    // Med ekte walk-frames bærer spriten selve bevegelsen — bare skyggen
    // pulserer. Uten frames gjør bob+vugg jobben.
    if (!this.avatarWalk) {
      this.avatarFigur.setY(-pose.bob);
      this.avatarFigur.setRotation(pose.vinkel);
    }
    this.avatarSkygge.setScale(pose.skyggeSkala);
    const skritt = skrittIndeks(elapsedMs);
    if (skritt !== this.sisteSkritt) {
      this.sisteSkritt = skritt;
      this.spruteStov();
    }
  }

  private nullstillGange(): void {
    this.sisteSkritt = 0;
    this.avatarFigur?.setY(0);
    this.avatarFigur?.setRotation(0);
    this.avatarSkygge?.setScale(1);
    if (this.avatarWalk) {
      this.avatarWalk.stop();
      this.avatarWalk.setFrame(0);
    }
  }

  /** Liten støvsky ved fotpunktet på hvert skritt-nedslag. */
  private spruteStov(): void {
    if (!this.avatar) return;
    const stov = this.add
      .ellipse(this.avatar.x, this.avatar.y + 4, 34, 12, 0x9a8c78, 0.22)
      .setDepth(this.avatar.y - 1);
    this.tweens.add({
      targets: stov,
      alpha: 0,
      scaleX: 1.8,
      scaleY: 1.4,
      duration: 340,
      ease: 'Quad.easeOut',
      onComplete: () => stov.destroy(),
    });
  }

  /** Skala (lineær med y) + depth-sortering: lenger ned = nærmere = foran. */
  private oppdaterAvatarPerspektiv(gangsone: GangsoneDef): void {
    if (!this.avatar) return;
    const minYs = this.bgY(gangsone.minY);
    const maxYs = this.bgY(gangsone.maxY);
    const t = Phaser.Math.Clamp((this.avatar.y - minYs) / Math.max(maxYs - minYs, 1), 0, 1);
    const s = gangsone.skalaMin + t * (gangsone.skalaMax - gangsone.skalaMin);
    this.avatar.setScale(s * this.avatarRetning, s);
    this.avatar.setDepth(this.avatar.y);
  }

  private byggTapToMove(gangsone: GangsoneDef): void {
    this.input.on('pointerup', (p: Phaser.Input.Pointer, over: unknown[]) => {
      if (over.length > 0) return; // dør-klikk håndteres av hitboxen
      if (p.y < HUD_HOYDE + BANNER_HOYDE) return;
      this.gaaTil(p.x, p.y, gangsone);
    });
  }

  /**
   * Tween-gange mot målet. Fotpunktet clampes alltid til gangbanen
   * [minY, maxY] — avataren kan aldri stå oppå en dør.
   */
  private gaaTil(
    malX: number,
    malY: number,
    gangsone: GangsoneDef,
    etterpa?: () => void,
  ): void {
    if (!this.avatar) return;
    const x = Phaser.Math.Clamp(malX, 70, GAME_WIDTH - 70);
    const y = Phaser.Math.Clamp(malY, this.bgY(gangsone.minY), this.bgY(gangsone.maxY));
    this.avatarTween?.stop();
    const avstand = Phaser.Math.Distance.Between(this.avatar.x, this.avatar.y, x, y);
    if (avstand < 4) {
      etterpa?.();
      return;
    }
    this.avatarRetning = x < this.avatar.x ? -1 : 1;
    if (foretrekkerRedusertBevegelse()) {
      this.avatar.setPosition(x, y);
      this.oppdaterAvatarPerspektiv(gangsone);
      etterpa?.();
      return;
    }
    this.sisteSkritt = 0;
    this.avatarWalk?.play(WALK_ANIM, true);
    this.avatarTween = this.tweens.add({
      targets: this.avatar,
      x,
      y,
      duration: avstand / GANGFART_PX_PER_MS,
      ease: 'Sine.easeInOut',
      onUpdate: (tween) => {
        this.oppdaterAvatarPerspektiv(gangsone);
        this.oppdaterGange(tween.elapsed);
      },
      onStop: () => this.nullstillGange(),
      onComplete: () => {
        this.nullstillGange();
        etterpa?.();
      },
    });
  }

  /** Tapp på dør = gå til gulvpunktet NEDENFOR døra, så bruk den. */
  private gaaTilDor(dor: DorDef): void {
    if (this.opptatt) return;
    const gangsone = (this.etasje ?? finnEtasje(5)!).korridor.gangsone;
    haptisk.lett();
    const malX = dor.rekt.x + dor.rekt.w / 2;
    const malY = this.bgY(gangsone.minY) + 14;
    this.gaaTil(malX, malY, gangsone, () => this.bruk(dor));
  }

  private bruk(dor: DorDef): void {
    if (this.opptatt) return;
    if (dor.type === 'laast') {
      sfx.play('click');
      this.toasts?.vis(dor.hint ?? 'Låst.', 'standard');
      return;
    }
    this.opptatt = true;
    sfx.play('click');
    if (dor.type === 'heis') {
      this.scene.start('Heis', { etasjeNr: this.etasje?.nr ?? 5 });
      return;
    }
    if (dor.rom) {
      // 2 min for å gå inn i et rom på samme etasje. Ankomst via heis er
      // allerede priset (5 min/etasje i HeisScene).
      const sammeEtasje = !this.ankomstViaHeis && gameState.aktivEtasje !== dor.rom;
      if (sammeEtasje) {
        gameClock.brukTid(TIDSKOSTNADER.romskifteSammeEtasje, `gå til ${dor.label}`);
      }
      gameState.setEtasje(dor.rom);
      this.scene.stop();
      this.scene.wake('Forlagshuset');
    }
  }
}
